/**
 * Video Call Page
 * Main page for starting/joining video calls
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Typography, Space, Card, message } from 'antd';
import { ArrowLeftOutlined, CopyOutlined } from '@ant-design/icons';
import { WebRTCMeeting } from '@/components/features/video-call/WebRTCMeeting';
import { VideoCallControls } from '@/components/features/video-call/VideoCallControls';
import { ActiveCallsList } from '@/components/features/video-call/ActiveCallsList';
import { useEndVideoCall } from '@/hooks/useVideoCall';
import { useVideoCallSocket } from '@/hooks/useVideoCallSocket';
import { useAuth } from '@/context/AuthContext';
import type { VideoCallMeeting } from '@/api/types/videocall.types';
import './VideoCallPage.css';

const { Title, Text } = Typography;

export const VideoCallPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mutate: endMeeting } = useEndVideoCall();

  const [inCall, setInCall] = useState(false);
  const [currentRoom, setCurrentRoom] = useState<string | null>(null);
  const [currentMeetingId, setCurrentMeetingId] = useState<string | null>(null);

  // Enable WebSocket notifications
  useVideoCallSocket({ enabled: true });

  // Check if joining from URL parameter
  useEffect(() => {
    const roomParam = searchParams.get('room');
    if (roomParam) {
      handleJoinRoom(roomParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleMeetingStarted = (roomName: string, meetingId: string) => {
    setCurrentRoom(roomName);
    setCurrentMeetingId(meetingId);
    setInCall(true);
  };

  const handleJoinRoom = (roomName: string) => {
    setCurrentRoom(roomName);
    setInCall(true);
  };

  const handleJoinActiveMeeting = (meeting: VideoCallMeeting) => {
    setCurrentRoom(meeting.room_name);
    setCurrentMeetingId(meeting.id);
    setInCall(true);
  };

  const handleLeaveCall = () => {
    if (currentMeetingId) {
      endMeeting(currentMeetingId);
    }
    setInCall(false);
    setCurrentRoom(null);
    setCurrentMeetingId(null);
    navigate('/apps/video-call', { replace: true });
  };

  const handleCopyLink = () => {
    if (!currentRoom) return;
    const meetingUrl = `${window.location.origin}/apps/video-call?room=${currentRoom}`;
    navigator.clipboard.writeText(meetingUrl);
    message.success('Meeting link copied to clipboard!');
  };

  const displayName = user?.full_name || user?.email || 'Anonymous';

  return (
    <div className="video-call-page">
      {!inCall ? (
        <>
          <div className="video-call-header">
            <Title level={2}>Video Calls</Title>
            <Text type="secondary">
              Start or join video meetings with your team
            </Text>
          </div>

          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <VideoCallControls onMeetingStarted={handleMeetingStarted} />
            <ActiveCallsList onJoinCall={handleJoinActiveMeeting} />
          </Space>
        </>
      ) : (
        <div className="video-call-active">
          <Card
            className="video-call-card"
            title={
              <Space>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={handleLeaveCall}
                  danger
                >
                  Leave Call
                </Button>
                <Text strong>{currentRoom}</Text>
              </Space>
            }
            extra={
              <Button icon={<CopyOutlined />} onClick={handleCopyLink}>
                Copy Link
              </Button>
            }
          >
            <WebRTCMeeting
              roomName={currentRoom!}
              userId={user?.id || ''}
              displayName={displayName}
              tenantId={user?.tenant_id || ''}
              onLeave={handleLeaveCall}
              onReady={() => console.log('Meeting ready')}
              onError={(error) => {
                console.error('WebRTC error:', error);
                message.error(`Video call error: ${error.message}`);
              }}
            />
          </Card>
        </div>
      )}
    </div>
  );
};
